import { extend, detectWidgetClick } from "@dhx/ts-common/core";
import { DataCollection, dragManager, DataEvents, DragEvents, IDataEventsHandlersMap,
	IDragEventsHandlersMap } from "@dhx/ts-data";

import { create, el, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { addHotkeys } from "@dhx/ts-common/Keymanager";
import { IHandlers, SelectionEvents } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";
import { Selection } from "./Selection";
import { isIE, locate, locateNode } from "@dhx/ts-common/html";
import { IListConfig, ListEvents, IListEventHandlersMap, ISelection, IList, IListItem } from "./types";
import { getEditor } from "./editors/editors";
import { defineValue, defineUnit } from "./helpers";

export class List extends View implements IList {
	public config: IListConfig;
	public data: DataCollection;
	public events: IEventSystem<DataEvents | ListEvents | DragEvents, IListEventHandlersMap & IDataEventsHandlersMap
		& IDragEventsHandlersMap>;
	public selection: ISelection;

	protected _handlers: IHandlers;

	private _range: [number, number];
	private _visibleHeight: number | string;
	private _topOffset: number;
	private _edited: string;

	private _navigationDestructor: () => void;

	private _widgetInFocus: boolean;
	private _documentClickDestuctor: () => void;

	private _focusIndex: number;

	constructor(node: HTMLElement | string, config: IListConfig = {}) {
		super(node, extend({
			itemHeight: config.virtual ? 34 : config.itemHeight || null,
			keyNavigation: false,
			multiselectionMode: config.multiselectionMode ? config.multiselectionMode : "click",
			editing: false
		}, config));

		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection({}, this.events);
			this.data.parse(this.config.data);
		} else if (this.config.data && this.config.data.events) {
			this.data = this.config.data;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection({}, this.events);
		}
		this.selection = new Selection({
			multiselection: this.config.multiselection,
			multiselectionMode: this.config.multiselectionMode,
		}, this.data);

		this._getHotkeys();

		const updater = (updateObj: any) => (id: string, ids?: any) => {
			if (ids && ids instanceof Array) {
				ids.map(selectedId => this.data.exists(selectedId) && this.data.update(selectedId, updateObj));
				return;
			}
			if (this.data.exists(id)) {
				this.data.update(id, updateObj);
			}
		};

		this.events.on(DataEvents.change, () => {
			if (this.config.virtual) {
				this._updateVirtual(0);
			}
			this.paint();
		});
		this.events.on(DataEvents.load, () => {
			this.data.map(item => {
				if (item.$selected) {
					this.selection.add(item.id);
					this.events.fire(ListEvents.click, [item.id, null]);
				}
			});
		});
		this.events.on(DragEvents.canDrop, updater({ $drophere: true }));
		this.events.on(DragEvents.cancelDrop, updater({ $drophere: undefined }));
		this.events.on(DragEvents.dragStart, updater({ $dragtarget: true }));
		this.events.on(DragEvents.dragEnd, updater({ $dragtarget: undefined }));
		this.events.on(ListEvents.afterEditEnd, (value, id) => {
			const item = this.data.getItem(id);
			this.data.update(id, { ...item, value });
			this._edited = null;
			this._getHotkeys();
			this.paint();
		});

		this.selection.events.on(SelectionEvents.afterSelect, id => {
			if (id) {
				this.setFocus(id);
			}
		});

		this._handlers = {
			onmousedown: (e: MouseEvent) => {
				const itemsForGhost = [];
				const item = locateNode(e, "dhx_id") as HTMLElement;
				const itemId = item && item.getAttribute("dhx_id");
				const selectionIds = this.selection.getId();
				if (this.config.multiselection && selectionIds instanceof Array) {
					selectionIds.map(id => {
						if (id !== itemId) {
							itemsForGhost.push(this.getRootView().refs[id].el);
						}
					});
				}
				return this.config.dragMode && !this._edited ? dragManager.onMouseDown(e, this.selection.getId(), itemsForGhost) : null;
			},
			ondragstart: () => this.config.dragMode && !this._edited ? false : null,
			oncontextmenu: (e: MouseEvent) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				this.events.fire(ListEvents.contextmenu, [id, e]);
			},
			onclick: (e: MouseEvent) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				this.selection.add(id, e.ctrlKey || e.metaKey, e.shiftKey);
				this.events.fire(ListEvents.click, [id, e]);
			},
			ondblclick: (e: MouseEvent) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				if (this.config.editing) {
					this.edit(id);
				}
				this.events.fire(ListEvents.doubleClick, [id, e]);
			},
			onscroll: (e: any) => this.config.virtual ? this._updateVirtual(e.target.scrollTop) : null
		};
		if (this.config.dragMode) {
			dragManager.setItem(this._uid, this);
		}

		if (this.config.virtual) {
			this._range = [0, 0];
			this._topOffset = 0;
		}

		const view = create({
			render: () => this.config.virtual ? this._renderVirtualList() : this._renderList(),
			hooks: {
				didMount: (vm: any) => {
					if (!this.config.height) {
						const element: HTMLElement = vm.node.el;
						this.config.height = (element && element.parentNode &&
							(element.parentNode as HTMLElement).offsetHeight) || 200;
					}
					if (this.config.virtual) {
						this._visibleHeight = this.config.height;
						this._updateVirtual(0);
					}
					this.paint();
				}
			}
		});
		this.mount(node, view);
	}
	edit(id: string): void {
		this._edited = id;
		if (!this.data.getItem(this._edited) || !this.events.fire(ListEvents.beforeEditStart, [id])) {
			this._edited = null;
			return;
		}
		this._getHotkeys();
		this.paint();
		this.events.fire(ListEvents.afterEditStart, [id]);
	}
	getFocusItem(): any {
		return this.data.getItem(this.data.getId(this._focusIndex));
	}
	setFocus(id: string): void {
		const index = this.data.getIndex(id);
		this._setFocusIndex(index);
	}
	getFocus(): string {
		const id = this.data.getId(this._focusIndex);
		if (id) {
			return id;
		}
	}
	destructor() {
		if (this._navigationDestructor) {
			this._navigationDestructor();
		}
		if (this._documentClickDestuctor) {
			this._documentClickDestuctor();
		}
		this.unmount();
	}
	// TODO: remove sute_7.0
	getFocusIndex() {
		return this._focusIndex;
	}
	// TODO: remove sute_7.0
	setFocusIndex(index: number): void {
		this._setFocusIndex(index);
	}
	protected _renderItem(item: IListItem, index: number): VNode {
		const html = (this.config.template && this.config.template(item)) || item.html;
		const focus = index === this._focusIndex;

		if (item.id === this._edited) {
			const editor = getEditor(item, this);
			return editor.toHTML();
		}
		return html ? this._renderAsHtml(html, item, focus) : this._renderAsValue(item, focus);
	}

	protected _renderAsHtml(html: string, item: IListItem, focus: boolean): VNode {
		const { itemHeight } = this.config;
		return el("li", {
			"class": "dhx_list-item" +
				(item.$selected ? " dhx_list-item--selected" : "") +
				(focus ? " dhx_list-item--focus" : "") +
				(item.$drophere && !this._edited ? " dhx_list-item--drophere" : "") +
				(item.$dragtarget && !this._edited ? " dhx_list-item--dragtarget" : "") +
				(this.config.dragMode && !this._edited ? " dhx_list-item--drag" : "") +
				// (this.selection.getItem() )
				(item.css ? " " + item.css : ""),
			"dhx_id": item.id,
			"_ref": item.id,
			"style": {
				height: itemHeight
			},
			"_key": item.id,
			".innerHTML": html
		},
		);
	}
	protected _renderAsValue(item: IListItem, focus: boolean): VNode {
		const { itemHeight } = this.config;
		return el("li", {
			class: "dhx_list-item dhx_list-item--text" +
				(item.$selected ? " dhx_list-item--selected" : "") +
				(focus ? " dhx_list-item--focus" : "") +
				(item.$drophere && !this._edited ? " dhx_list-item--drophere" : "") +
				(item.$dragtarget && !this._edited ? " dhx_list-item--dragtarget" : "") +
				(this.config.dragMode && !this._edited ? " dhx_list-item--drag" : "") +
				(item.css ? " " + item.css : ""),
			dhx_id: item.id,
			_ref: item.id,
			style: {
				height: itemHeight
			},
			_key: item.id,
		},
			item.text || item.value
		);
	}

	protected _renderList(): VNode {
		const kids = this.data.map((obj, index) => this._renderItem(obj, index));

		return el("ul.dhx_widget.dhx_list", {
			style: {
				"min-height": this.config.itemHeight,
				"max-height": this.config.height,
				"position": "relative"
			},
			class: this.config.css +
				(this.config.multiselection && this.selection.getItem() ? " dhx_no-select--pointer" : ""),
			dhx_widget_id: this._uid,
			...this._handlers
		}, kids);
	}
	protected _renderVirtualList(): VNode {
		const kids = this.data.mapRange(this._range[0], this._range[1], (obj, index) =>
			this._renderItem(obj, index));

		return el(".dhx_widget.dhx_virtual-list-wrapper", {
			dhx_widget_id: this._uid,
			style: {
				"min-height": this.config.itemHeight,
				"max-height": this._visibleHeight
			},
			...this._handlers
		},
			[
				el("ul.dhx_list.dhx_list--virtual", {
					class: this.config.css +
						(this.config.multiselection && this.selection.getItem() ? " dhx_no-select--pointer" : ""),
					style: {
						"height": this._getHeight() + defineUnit(this.config.itemHeight),
						"padding-top": this._topOffset
					},
				}, kids)
			]);
	}
	private _setFocusIndex(index: number): void {
		if (index < 0 || index > this.data.getLength() - 1) {
			return;
		}
		this._focusIndex = index;

		const rootView = this.getRootView();
		if (!rootView || !rootView.node || !rootView.node.el) {
			return;
		}

		const listEl: HTMLElement = this.getRootNode();
		if (!listEl) {
			return;
		}

		if (this.config.virtual) {
			const position = index * defineValue(this.config.itemHeight);
			if (position >= defineValue(this._visibleHeight) + this._topOffset || position < this._topOffset) {
				listEl.scrollTo(0, position);
			}
		} else {
			const listItem: HTMLElement = listEl.children[index] as HTMLElement;
			if (!listItem) {
				return;
			}
			if (listItem.offsetTop >= listEl.scrollTop + listEl.clientHeight - listItem.clientHeight) {
				listEl.scrollTop = listItem.offsetTop - listEl.clientHeight + listItem.clientHeight;
			} else if (listItem.offsetTop < listEl.scrollTop) {
				listEl.scrollTop = listItem.offsetTop;
			}
		}
		this.events.fire(ListEvents.focusChange, [this._focusIndex, this.data.getId(this._focusIndex)]);
		this.paint();
	}
	private _updateVirtual(position: number) {
		const overscanCount = 5;

		const totalHeight = this._getHeight();

		if (position > totalHeight - defineValue(this._visibleHeight)) {
			position = totalHeight - defineValue(this._visibleHeight);
		}

		const count = Math.floor(defineValue(this._visibleHeight) / defineValue(this.config.itemHeight))
			+ overscanCount;

		const index = Math.floor(position / defineValue(this.config.itemHeight));

		this._range = [index, count + index];

		this._topOffset = position;

		this.paint();
	}
	private _getHeight() {
		return this.data.getLength() * defineValue(this.config.itemHeight);
	}
	private _getHotkeys(): void {
		if (this.config.keyNavigation) {
			if (this._edited) {
				if (this._navigationDestructor) {
					this._navigationDestructor();
				}
			} else {
				let keyNavigation = this.config.keyNavigation as any;
				if (typeof this.config.keyNavigation !== "function") {
					this._widgetInFocus = false;
					keyNavigation = () => this._widgetInFocus;
					this._documentClickDestuctor = detectWidgetClick(this._uid, isInnerClick => this._widgetInFocus = isInnerClick);
				}
				const preventEvent = (fn: () => void) => (e: Event) => {
					e.preventDefault();
					fn();
				};
				let handlers: any = {
					"arrowDown": preventEvent(() => this.setFocusIndex(this._focusIndex + 1)),
					"arrowUp": preventEvent(() => this.setFocusIndex(this._focusIndex - 1)),
					"enter": (e: KeyboardEvent) => {
						const id = this.data.getId(this._focusIndex);
						this.selection.add(id);
						this.events.fire(ListEvents.click, [id, e]);
					},
					"enter+shift": (e: KeyboardEvent) => {
						const id = this.data.getId(this._focusIndex);
						this.selection.add(id, false, true);
						this.events.fire(ListEvents.click, [id, e]);
					},
					"enter+ctrl": (e: KeyboardEvent) => {
						const id = this.data.getId(this._focusIndex);
						this.selection.add(id, true, false);
						this.events.fire(ListEvents.click, [id, e]);
					}
				};
				if (isIE()) {
					handlers = { up: handlers.arrowUp, down: handlers.arrowDown, ...handlers };
					delete handlers.arrowUp;
					delete handlers.arrowDown;
				}
				this._navigationDestructor = addHotkeys(handlers, keyNavigation);
			}
		}
	}
}
